import React, { useEffect, useMemo } from 'react';
import SQLTable from '../../../components/sql_table/tableMain';
import { useApiData } from '../../../contexts/apiService';
import { useMonthNavigation } from '../../../contexts/monthNavigationContext';
import { useDataManager } from '../../../contexts/dataManager';
import { useUserAccess, USER_ROLE_LEVEL } from '../../../contexts/userAccess';
import { getColConfigs } from '../tableConfig';

const TABLE_NAME = 'vac_accounts_payable';

interface VacApModeProps {
    currentMonth?: string;
}

const VacApMode: React.FC<VacApModeProps> = ({ currentMonth }) => {
    const { apiData } = useApiData();
    const { requestTableData, hasTableData } = useDataManager();
    const { getMonthSelection } = useMonthNavigation();
    const { userRole = 'viewer', hasAdminAccess, hasAccAccess } = useUserAccess();
    const SCHEDULER = USER_ROLE_LEVEL['scheduler'];
    const showTable = Number(USER_ROLE_LEVEL[userRole]) >= SCHEDULER;

    const month = currentMonth ?? getMonthSelection(TABLE_NAME);

    useEffect(() => {
        if (month && !hasTableData(TABLE_NAME, month)) {
            requestTableData(TABLE_NAME, month);
        }
    }, [month]);  // eslint-disable-line react-hooks/exhaustive-deps

    const { reconciled, orphanedOI, orphanedDisp } = useMemo(() => {
        const data = (apiData as any)?.[TABLE_NAME];
        return {
            reconciled:   data?.reconciled        ?? [],
            orphanedOI:   data?.orphaned_oi       ?? [],
            orphanedDisp: data?.orphaned_disposal ?? [],
        };
    }, [apiData]);
    const COL_CONFIGS = useMemo(() => getColConfigs(TABLE_NAME, [], [], []), []);
    const userData = { role: userRole, hasAccAccess, hasAdminAccess };

    return (
        <div className="ticketData-tableContainer">
            <div className="ticketData-tableWrapper">
                <SQLTable
                    tableName={TABLE_NAME}
                    tableData={reconciled}
                    idColumn="rid"
                    columnConfigs={COL_CONFIGS[TABLE_NAME]?.columns ?? []}
                    sortOrder={COL_CONFIGS[TABLE_NAME]?.sortOrder}
                    enableSummary={true}
                    summaryConfig={COL_CONFIGS[TABLE_NAME]?.tableSummary}
                    monthNavigation={true}
                    enableWarnings={true}
                    persistFilters={true}
                    warningRules={COL_CONFIGS[TABLE_NAME]?.warningRules}
                    showTable={showTable}
                    rowColor={COL_CONFIGS[TABLE_NAME]?.rowColor}
                    userData={userData}
                />
            </div>

            {orphanedOI.length > 0 && (
                <details className="ticketData-orphanPanel">
                    <summary className="ticketData-orphanSummary">
                        Unmatched OI Tickets ({orphanedOI.length})
                    </summary>
                    <div className="ticketData-tableWrapper">
                        <SQLTable
                            tableName="vac_ap_orphaned_oi"
                            tableData={orphanedOI}
                            idColumn="id"
                            columnConfigs={COL_CONFIGS['vac_ap_orphaned_oi']?.columns ?? []}
                            sortOrder={COL_CONFIGS['vac_ap_orphaned_oi']?.sortOrder}
                            showTable={showTable}
                            userData={userData}
                        />
                    </div>
                </details>
            )}

            {orphanedDisp.length > 0 && (
                <details className="ticketData-orphanPanel">
                    <summary className="ticketData-orphanSummary">
                        Unmatched Disposal Tickets ({orphanedDisp.length})
                    </summary>
                    <div className="ticketData-tableWrapper">
                        <SQLTable
                            tableName="vac_ap_orphaned_disposal"
                            tableData={orphanedDisp}
                            idColumn="ID"
                            columnConfigs={COL_CONFIGS['vac_ap_orphaned_disposal']?.columns ?? []}
                            sortOrder={COL_CONFIGS['vac_ap_orphaned_disposal']?.sortOrder}
                            showTable={showTable}
                            userData={userData}
                        />
                    </div>
                </details>
            )}
        </div>
    );
};

export default VacApMode;
